import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FormContainer } from 'react-hook-form-mui'
import SkipNavigation from '../../components/SkipNavigation'
import CustomHeader from '../../components/CustomHeader'
import PageHeader from '../../components/PageHeader'
import { AcademicUnitAndInstructorAddedQuestionsFeature } from '../../features/AcademicUnitAndInstructorAddedQuestionsFeature'
import PAGE_TITLES from '../../consts/titles'
import useAsyncEffect from 'use-async-effect'
import { useDispatch } from 'react-redux'
import { resetState, setEditableSurvey } from '../../store/slices/SurveyTemplateSlice'
import SurveyHeader from './Header'
import { ADDED_QUESTION_TYPES } from '../../consts/addedQuestionTypes'
import LoadingPageV2 from '../LoadingPageV2'
import { AutoImportManager } from '../../APIManager'
import { notification } from '../../components/Notifications'
import { type ApiError } from '../../models/Common'
import ROUTES from '../../consts/paths'
import { SharedQuestionSetSwitcher, SharedQuestionsNotice } from './SharedQuestionSetSwitcher'
import { type AddedQuestionType } from '../../consts/addedQuestionTypes'
import { getSurveySections } from '../../store/slices/SurveyTemplateSlice/selectors'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import { Typography } from '@mui/material'
import _ from 'lodash'

const AcademicUnitAndInstructorAddedQuestions = (): React.ReactElement => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const slotId = params.get('slotId')
  const divisionId = params.get('divisionId')
  const categoryName = params.get('categoryName')
  const type = params.get('type')
  const addedQuestionCount = params.get('addedQuestionCount')
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  // CR-295: the shared set is a third `type`, loaded one at a time like the other two. The screen
  // keeps its singleton editable survey (`SurveyTemplateSlice`), so switching sets is a param
  // change and a refetch — not two simultaneously-editable sets, which would be a change of
  // ownership over shared survey-builder state.
  const isSectionType = type === ADDED_QUESTION_TYPES.SECTION

  const { data: adminQuestions, isLoading, isFetching, error: templateError } = useQuery({
    queryKey: [
      isSectionType
        ? 'sectionAddedQuestions'
        : type === ADDED_QUESTION_TYPES.INSTRUCTOR ? 'instructorQuestions' : 'adminQuestions',
      slotId,
      divisionId
    ],
    queryFn: async () => {
      if (isSectionType) {
        return await AutoImportManager.getSectionAddedQuestions(slotId ?? '', divisionId ?? '')
      }
      if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
        return await AutoImportManager.getAllInstructorQuestions(slotId ?? '', divisionId ?? '')
      }
      return await AutoImportManager.getAllAdminQuestions(slotId ?? '', divisionId ?? '')
    },
    // Instructor and section types: always fetch so the builder is visible even before any
    // questions are added (empty set). Admin type: keep the existing guard.
    enabled: !!slotId && !!divisionId && (
      type === ADDED_QUESTION_TYPES.INSTRUCTOR || isSectionType || Number(addedQuestionCount) > 0
    ),
    // Instructor 404 is the expected empty-slot state — don't retry it.
    retry: false,
    // Suppress the global error toast — this query handles its own errors
    // (instructor 404 is expected on first load and silently ignored).
    meta: { silent: true }
  })

  const isSectionScopedType = isSectionType || type === ADDED_QUESTION_TYPES.INSTRUCTOR
  // Both sets have to be available for a switcher to mean anything. With only the shared one, "Mine"
  // would invite an instructor to write questions the backend refuses to store — per-instructor
  // questions are off on that project — so the screen shows the shared set alone instead.
  const instructorSetAvailable = params.get('instructorSetAvailable') === 'true'

  // The other set, fetched for its count and — for the shared one — for the question limit that
  // tells us the type is on at all. Never loaded into the slice: fetching both is fine, it is
  // *editing* both that the singleton slice cannot support. Keyed like the active query so flipping
  // tabs reads the cache instead of refetching.
  const { data: siblingSet } = useQuery({
    queryKey: [isSectionType ? 'instructorQuestions' : 'sectionAddedQuestions', slotId, divisionId],
    queryFn: async () => {
      return isSectionType
        ? await AutoImportManager.getAllInstructorQuestions(slotId ?? '', divisionId ?? '')
        : await AutoImportManager.getSectionAddedQuestions(slotId ?? '', divisionId ?? '')
    },
    enabled: isSectionScopedType && !!slotId && !!divisionId,
    retry: false,
    // A missing instructor set is a 404 by design, and a missing shared set is `{}` — neither is
    // worth a toast when all we wanted was a number for a tab label.
    meta: { silent: true }
  })

  // Whichever response describes the shared set, whichever tab is active.
  const sectionSetResponse = isSectionType ? adminQuestions : siblingSet
  const sectionQuestionLimit = Number(sectionSetResponse?.data?.sectionQuestionCount) || undefined

  // Is the type on for this project? Two signals, both of which now stand for themselves:
  //
  //  - the card's `sectionTypeEnabled`, from `is_section_added_questions_allowed` on the dashboard
  //    payload;
  //  - `section_question_count` on the shared set's own response, since only an enabled type has a
  //    limit — which covers arriving here without that param.
  //
  // Neither is the shared-set pointer this used to lean on. That pointer meant "some section has
  // questions", and only implied enablement while enabling required a default template that seeded
  // every section — an invariant removed on 2026-08-22, after which a project could be on with a
  // limit and no template and the switcher would never appear.
  const isSectionTypeOn = params.get('sectionTypeEnabled') === 'true' || sectionQuestionLimit != null
  const isSwitchable = isSectionTypeOn && instructorSetAvailable && isSectionScopedType

  const countOf = (payload?: { data?: { questionCount?: number } }): number =>
    Number(payload?.data?.questionCount ?? 0)
  const activeCount = countOf(adminQuestions)
  const siblingCount = countOf(siblingSet)

  /**
   * Whether the loaded set has been edited, compared against the response it was seeded from.
   *
   * Switching sets reloads the slice — `resetState()` below is unconditional — so without this an
   * instructor who adds questions on one tab and clicks the other loses them with no prompt. The
   * tab counts come from the server, so they carry on reading the saved numbers and give no hint
   * anything was dropped.
   *
   * The baseline is the response rather than a snapshot taken when the slice was seeded: a ref
   * mutated in the seeding effect is only as current as the next render, and nothing guarantees one
   * happens between the mutation and a click on the switcher.
   */
  const surveySections = getSurveySections()
  const hasUnsavedChanges = !_.isEqual(
    adminQuestions?.data?.surveyItems ?? [],
    surveySections?.[0]?.surveyItems ?? []
  )
  const [pendingSetSwitch, setPendingSetSwitch] = React.useState<AddedQuestionType | null>(null)

  const applyQuestionSet = (nextType: AddedQuestionType): void => {
    const next = new URLSearchParams(search)
    next.set('type', nextType)
    // The route constant rather than `window.location.pathname`: the router owns this screen's
    // address, and reading it off `window` would also break the moment the page is mounted under a
    // different path.
    navigate(`${ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS}?${next.toString()}`, { replace: true })
  }

  const selectQuestionSet = (nextType: AddedQuestionType): void => {
    if (hasUnsavedChanges) {
      setPendingSetSwitch(nextType)
      return
    }
    applyQuestionSet(nextType)
  }

  React.useEffect(() => {
    if (Number(addedQuestionCount) === 0 && type !== ADDED_QUESTION_TYPES.INSTRUCTOR) {
      queryClient.removeQueries({
        queryKey: [
          'adminQuestions',
          slotId,
          divisionId
        ]
      })
    }
  }, [queryClient, addedQuestionCount, type, slotId, divisionId])

  React.useEffect(() => {
    if (templateError) {
      // Instructor type: a 404 means no slot exists yet — render the empty
      // builder so the instructor can add their first question.
      //
      // The section type does not need that arm: an absent shared set comes back as `{}` with a
      // 200, and its 404 means the section genuinely has no node in this project. The first save on
      // a set that does not exist is a POST to that same slot and section, so it would hit the same
      // wall — an empty builder here is a dead end where the instructor writes questions and only
      // then learns they cannot be stored. Navigating back is the kinder answer; it just has to say
      // why, rather than passing along a message written for whoever configured the project.
      const is404 = (templateError as ApiError)?.status === 404
      if (type === ADDED_QUESTION_TYPES.INSTRUCTOR && is404) return
      navigate(-1)
      notification(
        isSectionType && is404
          ? 'This course section is not part of this survey project.'
          : templateError.message,
        { variant: 'error' }
      )
    }
  }, [templateError, type, navigate])

  useAsyncEffect(() => {
    dispatch(resetState())
    // "No shared set yet" must not seed the slice: that makes `editableSurvey` truthy, and the
    // header reads exactly that to choose PATCH over POST — so the first save on an empty section
    // would PATCH a set that does not exist.
    //
    // Keyed on the set's own id rather than on the response being empty. The section route signals
    // absence with `{}` today, but it is about to start carrying the question limit in that same
    // empty case, and an emptiness check would then read "limit present" as "set exists". Asking
    // for the thing we actually need survives both shapes.
    const hasSet = adminQuestions?.data?.surveySectionId != null
    if (hasSet) {
      dispatch(setEditableSurvey({
        title: 'title',
        surveySections: [adminQuestions?.data]
      }))
    }
  }, [adminQuestions])

  if (isLoading || isFetching) {
    return <LoadingPageV2 />
  }

  return (
    <FormContainer>
      <SkipNavigation />
      <CustomHeader title={type === ADDED_QUESTION_TYPES.INSTRUCTOR || isSectionType ? PAGE_TITLES.INSTRUCTOR_ADDED_QUESTION : PAGE_TITLES.ADMIN_ADDED_QUESTION} />
      <PageHeader
        title={`${categoryName} Questions`}
        isCompactOnly
        showBackButton
        controls={
          <SurveyHeader />
        }
      />
      {pendingSetSwitch != null && (
        <GenericModal
          isOpen
          onClose={() => { setPendingSetSwitch(null) }}
          title="Discard unsaved questions?"
          maxWidth="sm"
          action={false}
        >
          <Typography fontSize="14px" mb={2}>
            Switching sets reloads this screen, so anything you have added or changed here and not
            saved will be lost.
          </Typography>
          <DefaultModalActions
            destructiveButtonProps={{
              onClick: () => { setPendingSetSwitch(null) }
            }}
            confirmButtonProps={{
              onClick: () => {
                applyQuestionSet(pendingSetSwitch)
                setPendingSetSwitch(null)
              },
              text: 'Discard and Switch'
            }}
          />
        </GenericModal>
      )}
      {isSwitchable && (
        <SharedQuestionSetSwitcher
          // `isSwitchable` already establishes that the active set is one of these two, so this is
          // derived rather than asserted from the raw search param.
          activeType={isSectionType ? ADDED_QUESTION_TYPES.SECTION : ADDED_QUESTION_TYPES.INSTRUCTOR}
          sharedCount={isSectionType ? activeCount : siblingCount}
          mineCount={isSectionType ? siblingCount : activeCount}
          onSelect={selectQuestionSet}
        />
      )}
      {isSectionType && <SharedQuestionsNotice hasMineTab={isSwitchable} />}
      <AcademicUnitAndInstructorAddedQuestionsFeature
        // The shared set's limit, read straight off the set's own response — `section_question_count`
        // there, distinct from `question_count`, which is how many questions have been added. Not
        // via the dashboard card: the card's serializer carries no SECTION_ADDED settings, by their
        // deliberate decision.
        sectionQuestionLimit={Number(adminQuestions?.data?.sectionQuestionCount) || undefined}
      />
    </FormContainer>
  )
}
export default AcademicUnitAndInstructorAddedQuestions
